"use client"

import { useState } from "react"
import { X } from "lucide-react"
import Button from "@mui/material/Button"

const experts = [
  {
    name: "Islam Hijawi",
    role: "CEO",
    image: "/Islam.png",
    description: `Extensive experience in health provider-payer solutions within the Gulf market, gained through collaborations with various leading organizations.`,
    fullBio: `Islam Hijawi brings extensive experience in health provider-payer solutions within the Gulf market, gained through collaborations with various leading organizations. His strategic vision and deep understanding of the healthcare landscape have been instrumental in positioning Kayan as a leader in e-claims management across the GCC region.`,
  },
  {
  name: "Diana Dyab",
  role: "CTO",
  image: "/diana.jpg",
  description: `Chief Technology Officer (CTO) | Software Engineer | Digital Innovator.`,
  fullBio: `Currently serving as Chief Technology Officer at Kayan Healthcare, leading technology strategy, driving digital innovation, and aligning technical vision with business objectives. Experienced in building scalable systems, overseeing full-stack development, optimizing platforms, and leading high-performing, inclusive engineering teams.`,
},

  {
    name: "Kawther Ghanem",
    role: "COO",
    image: "/kawther.jpg",
    description: `An Economics major with 7 years of experience in business management, bringing expertise in strategic planning and operational efficiency.`,
    fullBio: `Kawther Ghanem is an Economics major with 7 years of experience in business management, bringing expertise in strategic planning and operational efficiency. Her operational leadership ensures seamless execution of Kayan's services across multiple GCC markets, maintaining the highest standards of quality and compliance.`,
  },
  {
    name: "Dr. Randa Hanna",
    role: "CMO",
    image: "/randa.jpg",
    description: `A pharmacist with over 10 years of experience and a certified AAPC medical coder. Expertise in managing insurance operations.`,
    fullBio: `Dr. Randa Hanna is a pharmacist with over 10 years of experience and a certified AAPC medical coder. Her expertise in managing insurance operations and deep knowledge of medical coding standards ensures that Kayan's solutions maintain the highest accuracy in claims processing and regulatory compliance.`,
  },
]

const ExpertsSection = () => {
  const [openExpert, setOpenExpert] = useState(null)

  return (
    <section
      id="our-experts"
      className="relative w-full min-h-screen bg-gradient-to-r from-[#020817] via-[#041E2F] to-black pt-32 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">

        {/* Background glows */}
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-purple-500/5 rounded-full blur-3xl" />

        {/* Header */}
        <div className="text-center mb-14 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
            Our Senior Management Team
          </h2>
          <p className="text-gray-400 text-sm sm:text-lg">
            These people work on making our product best.
          </p>
        </div>

        {/* Experts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-12 lg:gap-x-16 lg:gap-y-16">
          {experts.map((expert, index) => (
            <div
              key={index}
              className={`flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 group text-center sm:text-left
              ${index % 2 === 0 ? "md:justify-self-end md:mr-10" : "md:justify-self-start md:ml-16"}`}
            >

              {/* Profile Image */}
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-400 via-purple-500 to-cyan-400 p-[3px] opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-full h-full rounded-full bg-[#0d1829]" />
                </div>

                <div className="relative w-28 h-28 sm:w-32 sm:h-32 lg:w-36 lg:h-36 rounded-full overflow-hidden border-[3px] border-transparent bg-gradient-to-br from-cyan-400/30 via-purple-500/30 to-cyan-400/30 p-[2px]">
                  <div className="w-full h-full rounded-full overflow-hidden bg-[#1a2332]">
                    <img
                      src={expert.image}
                      alt={expert.name}
                      className="w-full h-full object-cover object-[center_20%] group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                </div>

                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-400/20 to-purple-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
              </div>

              {/* Info */}
              <div className="flex flex-col">
                <h3 className="text-white font-bold text-lg sm:text-xl mb-1 group-hover:text-cyan-400 transition-colors">
                  {expert.name}
                </h3>
                <p className="text-cyan-400 text-sm mb-3 font-semibold">
                  {expert.role}
                </p>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed line-clamp-2 max-w-xs mb-4">
                  {expert.description}
                </p>

                <Button
                  onClick={() => setOpenExpert(expert)}
                  variant="outlined"
                  sx={{
                    minWidth: "90px",
                    fontSize: "0.85rem",
                    px: "12px",
                    py: "4px",
                    borderRadius: "20px",
                    color: "#22d3ee",
                    borderColor: "rgba(34,211,238,0.6)",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#22d3ee",
                      backgroundColor: "rgba(34,211,238,0.12)",
                    },
                  }}
                >
                  View Profile
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {openExpert && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setOpenExpert(null)}
        >
          <div
            className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-gradient-to-br from-[#0d1829] to-[#1a2332] border border-white/10 rounded-2xl p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpenExpert(null)}
              className="absolute top-3 right-3 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition"
            >
              <X size={26} />
            </button>

            <div className="flex flex-col items-center text-center">
              <div className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden border-4 border-cyan-400/30 mb-6">
                <img
                  src={openExpert.image}
                  alt={openExpert.name}
                  className="w-full h-full object-cover object-[center_20%]"
                />
              </div>

              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                {openExpert.name}
              </h3>

              <span className="inline-block px-4 py-1 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-semibold uppercase mb-6">
                {openExpert.role}
              </span>

         <p className="text-gray-300 text-sm sm:text-base leading-7 text-left max-w-prose tracking-normal">
  {openExpert.fullBio}
</p>



            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default ExpertsSection
